import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { parseQueryString } from '@zg-rentals/string-utils';
import { Box, Card, Page, Spacer, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { Container, Notification } from '@zg-rentals/zrs-comet';

import {
  HeadWrapper,
  LoadingConstants,
  logError,
  PermissionCheck,
  SpinnerContainer,
  USER_MANAGEMENT_PAGE,
  useRentalManagerUser,
  useRmwPaths,
  useRouter,
  UserPermissionsConstants,
} from '@zg-rentals/rental-manager-env';

import Infos from './Infos';
import Questions from './Questions';
import Role from './Role';
import PropertyPicker from './PropertyPicker';
import Permissions from './Permissions';
import Preview from './Preview';

import { styledAddUserPageStyles } from './StyledComponents';
import UserManagementActions from '../../redux/actions/UserManagementActions';
import { useApplicationPermissions } from '../../hooks/useApplicationPermissions';
import { emitCancelAddingNewUserEvent } from '../../modal/ClickstreamEvents';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

const AddUserPage = () => {
  const dispatch = useDispatch();
  // @ts-ignore
  const currentUser = useSelector((state) => state.userManagement.currentUser);
  const location = useLocation();
  const router = useRouter();
  const paths = useRmwPaths();
  const { user } = useRentalManagerUser();
  const userManagementPath = `${paths.userManagementPage}/${paths.userManagementUsers}`;
  const queryParams = parseQueryString(location.search);
  const isInvitation = location.pathname.includes(paths.userManagementAddUser);
  const isPending = !!queryParams.email;
  const isYou = !isInvitation && !isPending && user.userToken === currentUser.userToken;
  const [activeStep, setActiveStep] = React.useState(isInvitation ? 1 : 6);
  const { getUsersWithApplicationPermissionDetails } = useApplicationPermissions();
  const [error, setError] = React.useState('');
  const { enqueueToast } = useToast();

  React.useEffect(() => {
    setError('');
    if (isInvitation) {
      dispatch(UserManagementActions.initInvitation());
    } else if (isPending) {
      dispatch(UserManagementActions.getPendingProfile(queryParams.email));
      dispatch(UserManagementActions.getPendingUserProperties(queryParams.email));
    } else {
      dispatch(UserManagementActions.getCurrentUserInfo(queryParams.userBrand, queryParams.userToken));
      dispatch(UserManagementActions.getCurrentUserProperties(queryParams.userBrand, queryParams.userToken));
    }
    dispatch(UserManagementActions.getAvailableProperties('zillow', user.userToken));
    getUsersWithApplicationPermissionDetails();
    return () => {
      dispatch(UserManagementActions.currentUserInit());
    };
  }, [
    dispatch,
    isInvitation,
    isPending,
    queryParams.email,
    queryParams.userBrand,
    queryParams.userToken,
    user.userToken,
  ]);

  const handleGoToUserManagement = () => {
    emitCancelAddingNewUserEvent({ user });
    router.push(userManagementPath);
  };

  const handleSave = () => {
    setError('');
    dispatch(UserManagementActions.editableUserSave())
      // @ts-ignore
      .then(() => {
        if (currentUser.isPending || !currentUser.userToken) {
          enqueueToast(
            <Toast tone="success">
              You&apos;ve invited {currentUser.userName} to your company! We&apos;ll let you know when they accept.
            </Toast>,
          );
        }
        router.push(userManagementPath);
        return;
      })
      .catch((error: unknown) => {
        logError({
          error,
          errorType: 'UserManagementActions_editableUserSave',
          context: 'React component: AddUserPage',
        });
        setError('Something went wrong, please try again.');
      });
  };

  const isAdmin = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN];
  const allProperties = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES];

  const handleNextStep = () => {
    setActiveStep(activeStep + 1);
  };

  const handlePreviousStep = () => {
    setActiveStep(isInvitation ? activeStep - 1 : 6);
  };

  const handleSaveComplete = () => setActiveStep(6);

  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return <Infos onNextStep={() => setActiveStep(2)} onCancel={handleGoToUserManagement} />;
      case 2:
        return (
          <Role
            onNextStep={() => setActiveStep(isAdmin ? 6 : 3)}
            onCancel={handleGoToUserManagement}
            onBack={handlePreviousStep}
            handleSaveComplete={handleSaveComplete}
          />
        );
      case 3:
        return (
          <Questions
            onNextStep={() => setActiveStep(allProperties ? 5 : 4)}
            onCancel={handleGoToUserManagement}
            onBack={handlePreviousStep}
          />
        );
      case 4:
        return (
          <PropertyPicker
            onNextStep={handleNextStep}
            onCancel={handleGoToUserManagement}
            onBack={handlePreviousStep}
            isInvitation={isInvitation}
          />
        );
      case 5:
        return (
          <Permissions
            onNextStep={handleNextStep}
            onCancel={handleGoToUserManagement}
            handleSaveComplete={handleSaveComplete}
            onBack={() => {
              if (allProperties) {
                setActiveStep(3);
              } else {
                handlePreviousStep();
              }
            }}
          />
        );
      case 6:
        return (
          <Preview
            onEditRole={() => setActiveStep(2)}
            onEditAllProperties={() => setActiveStep(3)}
            onEditProperties={() => setActiveStep(4)}
            onSendInvite={handleSave}
            onBack={() => {
              if (isAdmin) {
                setActiveStep(2);
              } else if (allProperties) {
                setActiveStep(3);
              } else {
                handlePreviousStep();
              }
            }}
            onCancel={handleGoToUserManagement}
            onEditPermissions={() => setActiveStep(5)}
          />
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (isInvitation) {
      return 'Add a new user';
    }
    if (isYou) {
      return 'Your details';
    }
    return 'User details';
  };

  const renderContent = () => (
    <>
      {currentUser.userErrorMessage && (
        <Spacer css={{ marginBlock: 'xs' }}>
          <Notification type="error">{currentUser.userErrorMessage}</Notification>
        </Spacer>
      )}
      {error && (
        <Spacer css={{ marginBlock: 'xs' }}>
          <Notification type="error">{error}</Notification>
        </Spacer>
      )}
      <Text textStyle="heading-xs">{getTitle()}</Text>
      <Spacer css={{ marginBlockEnd: 'sm' }} />
      {!currentUser.userErrorMessage && renderStep()}
    </>
  );

  const { userLoadingState } = currentUser;
  const isLoading = userLoadingState === LoadingConstants.LOADING || userLoadingState === LoadingConstants.INITIAL;

  return (
    <Box className={styledAddUserPageStyles}>
      <HeadWrapper title="Add user" />
      <PermissionCheck route={USER_MANAGEMENT_PAGE}>
        <Container className="AddUserPage-container">
          <Page.Breadcrumb css={{ marginBlockEnd: 'lg', paddingInline: 'md' }}>
            <TextButton icon={<IconChevronLeftFilled />} asChild>
              <Link to={userManagementPath}>User management</Link>
            </TextButton>
          </Page.Breadcrumb>
          <Card css={{ marginBlockEnd: 'lg' }}>{isLoading ? <SpinnerContainer /> : renderContent()}</Card>
        </Container>
      </PermissionCheck>
    </Box>
  );
};

export default AddUserPage;
